import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDialog } from "@adora/shared/hooks/use-dialog";
import { Box, Button, Menu, MenuItem, Table } from "flux";
import useBrands from "../hooks/use-brands";
import { Brand } from "@adora/shared/types/brand";
import LaunchExperimentDialog from "../components/launch-experiments-dialog";
import BulkGenerationDialog from "../components/bulk-generation-dialog";
import Page from "@adora/shared/components/layout/page";
import { CreateBrandDialog, EditBrandDialog } from "../components/brand-dialog";

const Brands: React.FC = () => {
  const { brands, isLoading } = useBrands();
  const { openDialog } = useDialog();

  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedBrandId, setSelectedBrandId] = useState<number | null>(null);

  const handleRowClick = (brandId: number) => {
    navigate(`/brands/${brandId}/ad-asset-groups`);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, brandId: number) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedBrandId(brandId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedBrandId(null);
  };

  const handleViewAdAssets = () => {
    if (selectedBrandId) {
      navigate(`/brands/${selectedBrandId}/ad-asset-groups`);
    }
    handleMenuClose();
  };

  const handleLaunchExperiment = async () => {
    if (selectedBrandId) {
      handleMenuClose();
      const confirm = await openDialog(LaunchExperimentDialog, { brandId: selectedBrandId });
      if (confirm) {
        navigate("/batches");
      }
    }
  };

  const handleViewExperiments = () => {
    if (selectedBrandId) {
      navigate(`/brands/${selectedBrandId}/experiments`);
    }
    handleMenuClose();
  };

  const handleViewReports = () => {
    if (selectedBrandId) {
      navigate(`/brands/${selectedBrandId}/reports_settings`);
    }
    handleMenuClose();
  };

  const handleBulkUploadImages = async () => {
    if (selectedBrandId) {
      handleMenuClose();
      const confirm = await openDialog(BulkGenerationDialog, { brandId: selectedBrandId });
      if (confirm) {
        navigate("/batches");
      }
    }
  };

  const columns = [
    { field: "id", value: "ID", width: 100 },
    { field: "name", value: "Name", width: 500 },
    { field: "s3Bucket", value: "S3 Bucket", width: 400 },
    { field: "status", value: "Status", width: 150 },
    { field: "createdAt", value: "Created At", width: 200 },
    {
      field: "actions",
      value: "Actions",
      width: 50,
      renderCell: (row: Brand) => (
        <Button
          variant="text"
          color="secondary"
          size="medium"
          onClick={(e) => handleMenuOpen(e, row.id)}
          dataDogIdentifier="brands page ellipsis action"
        >
          …
        </Button>
      )
    }
  ];

  async function handleCreateBrand() {
    await openDialog(CreateBrandDialog);
  }

  async function handleEditBrand() {
    if (selectedBrandId) {
      const brand = brands?.find((b) => b.id === selectedBrandId);
      if (brand) {
        handleMenuClose();
        await openDialog(EditBrandDialog, { brand });
      }
    }
  }

  return (
    <Page
      title="Brands"
      titleBarActions={
        <Button variant="contained" onClick={handleCreateBrand} dataDogIdentifier="brands page create brand">
          Create Brand
        </Button>
      }
    >
      <Box>
        <Table
          columns={columns}
          rows={brands}
          handleRowClick={handleRowClick}
          isLoading={isLoading}
          dataDogIdentifier="brands"
        />
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        sx={{
          "& .MuiPaper-root": {
            minWidth: "110px"
          }
        }}
      >
        <MenuItem size="medium" onClick={handleViewAdAssets}>
          View Ad Asset Groups
        </MenuItem>
        <MenuItem size="medium" onClick={handleLaunchExperiment}>
          Launch Experiment
        </MenuItem>
        <MenuItem size="medium" onClick={handleViewExperiments}>
          View Experiments
        </MenuItem>
        <MenuItem size="medium" onClick={handleViewReports}>
          Report Settings
        </MenuItem>
        <MenuItem size="medium" onClick={handleBulkUploadImages}>
          Bulk Image Generation - CSV Upload
        </MenuItem>
        <MenuItem size="medium" onClick={handleEditBrand}>
          Edit
        </MenuItem>
      </Menu>
    </Page>
  );
};

export default Brands;
